import React, { useEffect, useId, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { FieldLabel } from "../ui/FormControls";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import {
  MAX_QUOTE_PHOTOS,
  PROJECT_IMAGE_MAX_UPLOAD_BYTES,
  projectImageOversizeMessage,
} from "../../lib/quotePhotoUploadLimits";

const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/jpg,image/png,image/webp";

type SelectedPhoto = {
  id: string;
  file: File;
  previewUrl: string;
};

let nextPhotoId = 0;

function createPhotoId(file: File): string {
  nextPhotoId += 1;
  return `${file.name}-${file.size}-${file.lastModified}-${nextPhotoId}`;
}

export type OptionalPhotoUploadFieldProps = {
  id?: string;
  name: string;
  label?: string;
  description?: string;
  maxPhotos?: number;
  className?: string;
  inputClassName?: string;
  onValidationError?: (message: string) => void;
};

function syncFileInput(input: HTMLInputElement, files: File[]) {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  input.files = dataTransfer.files;
}

export const OptionalPhotoUploadField: React.FC<
  OptionalPhotoUploadFieldProps
> = ({
  id: idProp,
  name,
  label = "Project photos (optional)",
  description = "Add photos of the job site or project area to help us prepare a more accurate quote.",
  maxPhotos = MAX_QUOTE_PHOTOS,
  className,
  inputClassName,
  onValidationError,
}) => {
  const autoId = useId();
  const inputId = idProp ?? `photo-upload-${autoId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );

  useEffect(() => {
    return () => {
      photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    };
  }, [photos]);

  const reportError = (message: string) => {
    setValidationMessage(message);
    onValidationError?.(message);
  };

  const updatePhotos = (nextPhotos: SelectedPhoto[]) => {
    setPhotos((prev) => {
      prev.forEach((photo) => {
        if (!nextPhotos.some((p) => p.id === photo.id)) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
      return nextPhotos;
    });
    if (inputRef.current) {
      syncFileInput(
        inputRef.current,
        nextPhotos.map((photo) => photo.file),
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length === 0) return;

    const remaining = maxPhotos - photos.length;
    if (remaining <= 0) {
      reportError(`You can add up to ${maxPhotos} photos.`);
      if (inputRef.current)
        syncFileInput(
          inputRef.current,
          photos.map((p) => p.file),
        );
      return;
    }

    const accepted: SelectedPhoto[] = [];
    for (const file of selected.slice(0, remaining)) {
      if (file.size > PROJECT_IMAGE_MAX_UPLOAD_BYTES) {
        reportError(projectImageOversizeMessage(file.name));
        continue;
      }
      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
        reportError(`"${file.name}" must be a JPEG, PNG, or WebP image.`);
        continue;
      }
      accepted.push({
        id: createPhotoId(file),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (accepted.length === 0) {
      if (inputRef.current)
        syncFileInput(
          inputRef.current,
          photos.map((p) => p.file),
        );
      return;
    }

    setValidationMessage(null);
    updatePhotos([...photos, ...accepted]);
  };

  const removePhoto = (photoId: string) => {
    updatePhotos(photos.filter((photo) => photo.id !== photoId));
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div>
        <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        className="sr-only"
        onChange={handleFileChange}
      />

      {photos.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((photo) => (
            <li key={photo.id} className="relative">
              <img
                src={photo.previewUrl}
                alt=""
                className={cn(
                  "aspect-square w-full border border-border/70 object-cover",
                  inputClassName,
                )}
              />
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-2 top-2 h-8 w-8 rounded-full shadow-sm"
                aria-label="Remove photo"
                onClick={() => removePhoto(photo.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {validationMessage ? (
        <p className="text-sm text-destructive" role="alert">
          {validationMessage}
        </p>
      ) : null}

      {photos.length < maxPhotos ? (
        <Button
          type="button"
          variant="outline"
          className={cn("w-full justify-center gap-2", inputClassName)}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus className="h-4 w-4" />
          {photos.length === 0 ? "Add photos" : "Add more photos"}
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          Maximum of {maxPhotos} photos reached.
        </p>
      )}
    </div>
  );
};
